import React, { useEffect, useId, useMemo, useState } from "react";
import { useI18n } from "../i18n";
import "./UserBio.css";

export const BIO_PREVIEW_LENGTH = 100;
export const BIO_MAX_LENGTH = 1000;
export const EMPTY_BIO_TEXT = "This user has not shared anything about themselves yet.";

function normalizeBio(bio) {
  return typeof bio === "string" ? bio.trim() : "";
}

function createPreview(bio) {
  const characters = Array.from(bio);

  if (characters.length <= BIO_PREVIEW_LENGTH) {
    return bio;
  }

  return `${characters.slice(0, BIO_PREVIEW_LENGTH).join("").trimEnd()}…`;
}

function UserBio({ bio, username = "User" }) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const titleId = useId();
  const dialogId = useId();

  const normalizedBio = useMemo(() => normalizeBio(bio), [bio]);
  const characters = useMemo(() => Array.from(normalizedBio), [normalizedBio]);
  const hasLongBio = characters.length > BIO_PREVIEW_LENGTH;
  const displayedBio = normalizedBio ? createPreview(normalizedBio) : EMPTY_BIO_TEXT;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="user-bio-section">
      <p className="user-bio-preview" title={hasLongBio ? normalizedBio : undefined}>
        {displayedBio}
      </p>

      <div className="user-bio-more-row">
        {hasLongBio && (
          <button
            type="button"
            className="user-bio-more-button"
            onClick={() => setIsOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-controls={isOpen ? dialogId : undefined}
          >
            {t("profilePreview.readMore")}
          </button>
        )}
      </div>

      {isOpen && (
        <div
          className="user-bio-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          <div
            id={dialogId}
            className="user-bio-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="user-bio-modal-header">
              <h2 id={titleId}>About {username}</h2>

              <button
                type="button"
                className="user-bio-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close bio"
                autoFocus
              >
                ×
              </button>
            </div>

            <p className="user-bio-modal-text">{normalizedBio}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserBio;
